import React from "react"
import Button from "../Button/Button"
import {Application, ApplicationStatus, Assignee} from "../../resources/application"
import {ProductApplication, ProductApplicationStatus} from "../../resources/productApplication"
import {useOrgSettings} from "../../services/orgAuth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import {useIsAllowResubmit} from "../../services/permission"

export type CompletePendingTarget = Application | ProductApplication

interface CompletePendingButtonProps {
	application: CompletePendingTarget
	buttonOnClick: () => void
}

const isPending = (status: ApplicationStatus | ProductApplicationStatus) =>
	status === ApplicationStatus.Pending || status === ProductApplicationStatus.Pending

const CompletePendingButtonInner = ({application, buttonOnClick}: CompletePendingButtonProps) => {
	const orgSettingsAsyncResult = useOrgSettings(application.relationships.org.data.id)
	const isApplicationPending = isPending(application.attributes.status)

	return (
		<HasPermission resource="orgSettings" action="get">
			<AsyncResultComponent asyncResult={orgSettingsAsyncResult} pendingComponent={<></>}>
				{({value: orgSettings}) => {
					const canShowButton =
						isApplicationPending &&
						useIsAllowResubmit(
							orgSettings.attributes.generalSettings.enableReevaluateApplication,
							application.relationships.org.data.id,
							application.attributes.assignee ?? Assignee.Unit
						)

					return canShowButton ? (
						<Button
							tooltipPlace="bottom"
							theme="neutral"
							variant="outline"
							onClick={buttonOnClick}
							prefixIcon="pencil-1--interface-essential"
							className="mr-2"
						>
							<span>Complete Pending</span>
						</Button>
					) : (
						<React.Fragment />
					)
				}}
			</AsyncResultComponent>
		</HasPermission>
	)
}

// Outer wrapper guards on evaluation data before mounting the inner component,
// so useOrgSettings only fires when the application actually needs the button.
const CompletePendingButton = ({application, buttonOnClick}: CompletePendingButtonProps) => {
	const hasEvaluationData = !!application.attributes.evaluationEntityId && !!application.attributes.evaluationId

	if (!hasEvaluationData) {
		return <React.Fragment />
	}

	return <CompletePendingButtonInner application={application} buttonOnClick={buttonOnClick} />
}

export default CompletePendingButton
